import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import {
    Plus,
    Trash2,
    KeyRound,
    Save,
    ChevronDown,
    ChevronRight,
    Hash,
    LineChart,
    AlertCircle,
    BellRing,
    Send,
} from "lucide-react";

const emptyClient = {
    name: "",
    email: "",
    instagram_handle: "",
    password: "",
    is_demo: false,
};

const emptyMetric = {
    followers: 0,
    engagement_rate: 0,
    avg_likes: 0,
    avg_comments: 0,
    reel_views: 0,
    posting_frequency: "",
    recorded_at: "",
};

function formatDate(iso) {
    if (!iso) return "Never";
    try {
        return new Date(iso).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function ClientsPanel() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [addForm, setAddForm] = useState(null);
    const [alerts, setAlerts] = useState(null);
    const [sendingDigest, setSendingDigest] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/clients");
            setList(data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
        try {
            const { data } = await api.get("/admin/alerts/stale-preview");
            setAlerts(data);
        } catch {
            /* ignore — alerts preview is non-critical */
        }
    };

    useEffect(() => {
        load();
    }, []);

    const createClient = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/clients", addForm);
            toast.success("Client created");
            setAddForm(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const sendDigest = async () => {
        setSendingDigest(true);
        try {
            const { data } = await api.post(
                "/admin/alerts/send-digest?force=true",
            );
            if (data.sent) {
                toast.success(
                    `Digest sent to ${data.recipient} · ${data.stale_count} client${
                        data.stale_count === 1 ? "" : "s"
                    } listed`,
                );
            } else {
                toast.message(
                    "Email key not configured — digest logged to backend instead",
                    {
                        description: `${data.stale_count} client${
                            data.stale_count === 1 ? "" : "s"
                        } would be included.`,
                    },
                );
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSendingDigest(false);
        }
    };

    return (
        <div>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-display text-2xl">Clients · {list.length}</h2>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                        Red dot = last metric update is over 7 days old.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {alerts && (
                        <div
                            data-testid="admin-alerts-preview"
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border text-xs ${
                                alerts.count > 0
                                    ? "border-red-500/40 bg-red-500/10 text-red-200"
                                    : "border-[var(--border-subtle)] text-[var(--text-secondary)]"
                            }`}
                            title={`Threshold: ${alerts.threshold_days} days · Recipient: ${alerts.recipient}`}
                        >
                            <BellRing size={13} />
                            {alerts.count === 0
                                ? "All clients fresh"
                                : `${alerts.count} pending update`}
                        </div>
                    )}
                    <button
                        data-testid="admin-send-digest"
                        onClick={sendDigest}
                        disabled={sendingDigest}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--border-accent)] text-[var(--accent-champagne)] text-xs uppercase tracking-[0.2em] hover:bg-[rgba(212,175,55,0.08)] disabled:opacity-60"
                        title="Send the stale-client digest email right now"
                    >
                        <Send size={13} /> {sendingDigest ? "Sending…" : "Send digest"}
                    </button>
                    <button
                        data-testid={ADMIN.addClient}
                        onClick={() => setAddForm({ ...emptyClient })}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold text-xs uppercase tracking-[0.2em] hover:bg-[var(--accent-champagne)]"
                    >
                        <Plus size={14} /> New Client
                    </button>
                </div>
            </div>

            {addForm && (
                <form
                    onSubmit={createClient}
                    className="mb-6 rounded-2xl border border-[var(--border-accent)] bg-[rgba(212,175,55,0.05)] p-5 grid grid-cols-1 md:grid-cols-12 gap-3"
                >
                    <input
                        data-testid="admin-new-client-name"
                        required
                        placeholder="Client name"
                        value={addForm.name}
                        onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                        className="md:col-span-3 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                    />
                    <input
                        data-testid="admin-new-client-email"
                        required
                        type="email"
                        placeholder="Email"
                        value={addForm.email}
                        onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                        className="md:col-span-3 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                    />
                    <input
                        placeholder="@instagram"
                        value={addForm.instagram_handle}
                        onChange={(e) =>
                            setAddForm({ ...addForm, instagram_handle: e.target.value })
                        }
                        className="md:col-span-3 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                    />
                    <input
                        data-testid="admin-new-client-password"
                        required
                        type="text"
                        placeholder="Temp password (min 6)"
                        value={addForm.password}
                        onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                        className="md:col-span-3 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                    />
                    <div className="md:col-span-12 flex items-center gap-3 justify-end">
                        <button
                            type="button"
                            onClick={() => setAddForm(null)}
                            className="px-4 py-2 rounded-full border border-[var(--border-subtle)] text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="admin-new-client-submit"
                            type="submit"
                            className="px-5 py-2 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold text-xs uppercase tracking-[0.2em]"
                        >
                            Create client
                        </button>
                    </div>
                </form>
            )}

            {loading ? (
                <p className="text-[var(--text-secondary)]">Loading…</p>
            ) : list.length === 0 ? (
                <div className="rounded-xl border border-[var(--border-subtle)] p-8 text-center text-[var(--text-secondary)]">
                    No clients yet. Click &quot;New Client&quot; to add your first one.
                </div>
            ) : (
                <div className="space-y-3">
                    {list.map((c) => (
                        <ClientRow
                            key={c.id}
                            item={c}
                            expanded={expandedId === c.id}
                            onToggle={() =>
                                setExpandedId(expandedId === c.id ? null : c.id)
                            }
                            onChange={load}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function ClientRow({ item, expanded, onToggle, onChange }) {
    const [form, setForm] = useState({
        name: item.name || "",
        email: item.email || "",
        instagram_handle: item.instagram_handle || "",
        active: item.active,
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/clients/${item.id}`, form);
            toast.success("Client saved");
            onChange();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const del = async () => {
        if (
            !window.confirm(
                `Delete "${item.name}"? This wipes their metrics and hashtags too.`,
            )
        )
            return;
        try {
            await api.delete(`/admin/clients/${item.id}`);
            toast.success("Client deleted");
            onChange();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const resetPassword = async () => {
        const pw = window.prompt(
            `Set a new password for ${item.name}. Share it with them securely.`,
        );
        if (!pw) return;
        if (pw.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        try {
            await api.post(`/admin/clients/${item.id}/reset-password`, { password: pw });
            toast.success("Password reset. Share it with the client.");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div
            data-testid={`admin-client-row-${item.id}`}
            className="rounded-xl border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.02)]"
        >
            {/* Summary row */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 p-4">
                <button
                    onClick={onToggle}
                    className="md:col-span-1 flex items-center gap-2 text-[var(--accent-champagne)] hover:text-[var(--accent-gold)]"
                    aria-label={expanded ? "Collapse" : "Expand"}
                >
                    {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    {item.stale && (
                        <span
                            data-testid={`admin-client-stale-${item.id}`}
                            title="Metrics are over 7 days old"
                            className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-[0_0_10px_rgba(255,80,80,0.7)]"
                        />
                    )}
                </button>
                <input
                    className="md:col-span-2 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <input
                    className="md:col-span-3 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <input
                    className="md:col-span-2 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                    value={form.instagram_handle}
                    placeholder="@handle"
                    onChange={(e) =>
                        setForm({ ...form, instagram_handle: e.target.value })
                    }
                />
                <div className="md:col-span-2 text-xs text-[var(--text-secondary)] flex flex-col justify-center">
                    <span className="uppercase tracking-[0.2em] text-[10px] text-[var(--accent-champagne)]/70">
                        Last update
                    </span>
                    <span
                        className={
                            item.stale
                                ? "text-red-300"
                                : "text-[var(--text-primary)]"
                        }
                    >
                        {formatDate(item.last_updated)}
                    </span>
                </div>
                <label className="md:col-span-1 flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[var(--text-secondary)]">
                    <input
                        type="checkbox"
                        checked={form.active}
                        onChange={(e) =>
                            setForm({ ...form, active: e.target.checked })
                        }
                        className="accent-[var(--accent-gold)]"
                    />
                    Active
                </label>
                <div className="md:col-span-1 flex gap-1.5 justify-end">
                    <button
                        onClick={save}
                        disabled={saving}
                        title="Save profile"
                        className="p-2 rounded-lg bg-[var(--accent-gold)] text-[var(--brand-dark)] hover:bg-[var(--accent-champagne)] disabled:opacity-70"
                    >
                        <Save size={14} />
                    </button>
                    <button
                        onClick={resetPassword}
                        title="Manual password reset"
                        data-testid={`admin-client-reset-pw-${item.id}`}
                        className="p-2 rounded-lg border border-[var(--border-subtle)] text-[var(--accent-champagne)] hover:border-[var(--accent-gold)]"
                    >
                        <KeyRound size={14} />
                    </button>
                    <button
                        onClick={del}
                        title="Delete"
                        className="p-2 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>

            {/* Expanded panel */}
            {expanded && (
                <div className="border-t border-[var(--border-subtle)] p-5 grid grid-cols-1 lg:grid-cols-2 gap-6 bg-[rgba(0,0,0,0.25)]">
                    <MetricsEditor clientId={item.id} onChange={onChange} />
                    <HashtagsEditor clientId={item.id} />
                </div>
            )}
        </div>
    );
}

function MetricsEditor({ clientId, onChange }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ ...emptyMetric });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/clients/${clientId}/metrics`);
            setRows(data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, [clientId]);

    const add = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                followers: Number(form.followers) || 0,
                engagement_rate: Number(form.engagement_rate) || 0,
                avg_likes: Number(form.avg_likes) || 0,
                avg_comments: Number(form.avg_comments) || 0,
                reel_views: Number(form.reel_views) || 0,
                posting_frequency: form.posting_frequency || "",
                recorded_at: form.recorded_at || undefined,
            };
            await api.post(`/admin/clients/${clientId}/metrics`, payload);
            toast.success("Snapshot added");
            setForm({ ...emptyMetric });
            load();
            onChange && onChange();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const del = async (mid) => {
        if (!window.confirm("Delete this snapshot?")) return;
        try {
            await api.delete(`/admin/metrics/${mid}`);
            load();
            onChange && onChange();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const fields = [
        ["followers", "Followers", "number"],
        ["engagement_rate", "Eng. Rate %", "number"],
        ["avg_likes", "Avg Likes", "number"],
        ["avg_comments", "Avg Comments", "number"],
        ["reel_views", "Reel Views", "number"],
        ["posting_frequency", "Frequency", "text"],
    ];

    return (
        <div>
            <div className="flex items-center gap-2 mb-4">
                <LineChart size={16} className="text-[var(--accent-gold)]" />
                <h3 className="font-display text-lg text-[var(--text-primary)]">
                    Weekly Metrics
                </h3>
            </div>

            <form
                data-testid={`admin-add-metric-${clientId}`}
                onSubmit={add}
                className="grid grid-cols-2 gap-2 mb-4"
            >
                {fields.map(([k, l, t]) => (
                    <label key={k} className="block">
                        <span className="block text-[10px] uppercase tracking-[0.2em] text-[var(--accent-champagne)]/60 mb-1">
                            {l}
                        </span>
                        <input
                            type={t}
                            step={k === "engagement_rate" ? "0.01" : "1"}
                            value={form[k]}
                            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                            className="w-full bg-transparent border border-[var(--border-subtle)] rounded-lg px-2.5 py-1.5 text-sm"
                        />
                    </label>
                ))}
                <label className="col-span-2 block">
                    <span className="block text-[10px] uppercase tracking-[0.2em] text-[var(--accent-champagne)]/60 mb-1">
                        Recorded At (optional, defaults to now)
                    </span>
                    <input
                        type="datetime-local"
                        value={form.recorded_at}
                        onChange={(e) =>
                            setForm({ ...form, recorded_at: e.target.value })
                        }
                        className="w-full bg-transparent border border-[var(--border-subtle)] rounded-lg px-2.5 py-1.5 text-sm"
                    />
                </label>
                <button
                    type="submit"
                    className="col-span-2 inline-flex items-center justify-center gap-2 py-2 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] text-xs font-semibold uppercase tracking-[0.2em]"
                >
                    <Plus size={13} /> Add snapshot
                </button>
            </form>

            <div className="max-h-64 overflow-y-auto rounded-lg border border-[var(--border-subtle)]">
                {loading ? (
                    <p className="p-3 text-xs text-[var(--text-secondary)]">Loading…</p>
                ) : rows.length === 0 ? (
                    <div className="p-4 text-xs text-[var(--text-secondary)] inline-flex items-center gap-2">
                        <AlertCircle size={14} className="text-amber-400" />
                        No metrics yet — client will see the empty state.
                    </div>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="bg-[rgba(255,255,255,0.02)] text-[10px] uppercase tracking-[0.15em] text-[var(--accent-champagne)]/70">
                            <tr>
                                <th className="text-left px-3 py-2">Date</th>
                                <th className="text-right px-3 py-2">Followers</th>
                                <th className="text-right px-3 py-2">ER%</th>
                                <th className="text-right px-3 py-2">Reels</th>
                                <th className="px-2 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr
                                    key={r.id}
                                    className="border-t border-[var(--border-subtle)]"
                                >
                                    <td className="px-3 py-1.5 text-[var(--text-secondary)] whitespace-nowrap">
                                        {formatDate(r.recorded_at)}
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                        {r.followers?.toLocaleString?.() ?? r.followers}
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                        {r.engagement_rate}
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                        {r.reel_views?.toLocaleString?.() ?? r.reel_views}
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <button
                                            onClick={() => del(r.id)}
                                            className="p-1 rounded border border-red-500/30 text-red-300 hover:bg-red-500/10"
                                            title="Delete snapshot"
                                        >
                                            <Trash2 size={11} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function HashtagsEditor({ clientId }) {
    const [rows, setRows] = useState([]);
    const [tag, setTag] = useState("");
    const [rank, setRank] = useState(1);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/clients/${clientId}/hashtags`);
            setRows(data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, [clientId]);

    const add = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/admin/clients/${clientId}/hashtags`, {
                hashtag: tag.trim(),
                performance_rank: Number(rank) || 1,
            });
            setTag("");
            setRank((rows.length || 0) + 2);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const del = async (hid) => {
        try {
            await api.delete(`/admin/hashtags/${hid}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div>
            <div className="flex items-center gap-2 mb-4">
                <Hash size={16} className="text-[var(--accent-gold)]" />
                <h3 className="font-display text-lg text-[var(--text-primary)]">
                    Top Hashtags
                </h3>
            </div>

            <form onSubmit={add} className="flex gap-2 mb-4">
                <input
                    placeholder="#organicgrowth"
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    required
                    className="flex-1 bg-transparent border border-[var(--border-subtle)] rounded-lg px-3 py-2 text-sm"
                />
                <input
                    type="number"
                    min="1"
                    value={rank}
                    onChange={(e) => setRank(e.target.value)}
                    className="w-16 bg-transparent border border-[var(--border-subtle)] rounded-lg px-2 py-2 text-sm text-center"
                    title="Rank (1 = top)"
                />
                <button
                    type="submit"
                    className="px-4 py-2 rounded-lg bg-[var(--accent-gold)] text-[var(--brand-dark)] text-xs font-semibold uppercase tracking-[0.2em]"
                >
                    Add
                </button>
            </form>

            {loading ? (
                <p className="text-xs text-[var(--text-secondary)]">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="text-xs text-[var(--text-secondary)]">No hashtags yet.</p>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {rows.map((h) => (
                        <span
                            key={h.id}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--border-accent)] bg-[rgba(212,175,55,0.08)] text-[var(--accent-champagne)] text-xs"
                        >
                            <span className="text-[10px] text-[var(--accent-gold)]">
                                #{h.performance_rank}
                            </span>
                            {h.hashtag}
                            <button
                                onClick={() => del(h.id)}
                                className="text-red-300 hover:text-red-200"
                                aria-label="Remove hashtag"
                            >
                                <Trash2 size={11} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
